"use client";

import { useCallback, useState } from "react";

import { api } from "@/trpc/react";

/**
 * Which announcements a visitor has already been shown.
 *
 * Signed-in visitors are tracked in the database (`seen_announcements`), so an announcement is
 * offered once per person rather than once per browser — see it on a laptop and it stays gone on a
 * phone. Signed-out visitors have no row to write to, so they fall back to localStorage; that is
 * the only option available, and enough for a card nobody is logged in to see twice.
 */

const STORAGE_KEY = "youlli:seen-announcements";

function readLocal(): string[] {
  // Touching localStorage can itself throw — private windows, storage blocked by policy — so the
  // access is guarded, not just its result.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

function writeLocal(ids: readonly string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // The modal still closes for this page view; the announcement just comes back on the next one.
  }
}

export interface SeenAnnouncements {
  /** `null` while still unknown, so the caller offers nothing rather than guessing. */
  readonly seen: string[] | null;
  readonly markSeen: (announcementIds: readonly string[]) => void;
}

function useLocalSeen(): SeenAnnouncements {
  /**
   * Read lazily rather than in an effect: the initialiser runs before the first paint on the
   * client, and on the server it is never reached, so there is no window in which the modal opens
   * and then vanishes.
   */
  const [seen, setSeen] = useState<string[] | null>(() =>
    typeof window === "undefined" ? null : readLocal(),
  );

  const markSeen = useCallback((announcementIds: readonly string[]) => {
    setSeen((current) => {
      const next = [...new Set([...(current ?? []), ...announcementIds])];
      writeLocal(next);
      return next;
    });
  }, []);

  return { seen, markSeen };
}

export function useSeenAnnouncements(isSignedIn: boolean): SeenAnnouncements {
  const local = useLocalSeen();

  const query = api.announcements.seen.useQuery(undefined, {
    enabled: isSignedIn,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  const { mutate } = api.announcements.markSeen.useMutation();

  /**
   * Ids marked during this page view. Held locally as well as sent to the server so the caller
   * stops re-offering them immediately, without waiting for the round trip.
   */
  const [justSeen, setJustSeen] = useState<string[]>([]);

  const markSeen = useCallback(
    (announcementIds: readonly string[]) => {
      const ids = [...announcementIds];
      if (ids.length === 0) return;

      setJustSeen((current) => [...new Set([...current, ...ids])]);
      // A failed write leaves the announcement gone for this page view and offered again on the
      // next one. That is the right way round: re-reading an announcement is a smaller harm than a
      // modal that will not close.
      mutate({ announcementIds: ids });
    },
    [mutate],
  );

  if (!isSignedIn) return local;

  return {
    seen: query.isPending ? null : [...(query.data ?? []), ...justSeen],
    markSeen,
  };
}
